import { usePost } from "@albertine/api-client";
import { type Dispatch, type SetStateAction, useEffect } from "react";
import { type UseFormReturn, useWatch } from "react-hook-form";
import { useDebounce } from "../../utils/customHook.util";
import {
    type ContactDetailsInUse,
    emailInUseMessage,
    NewMemberEmail,
    type NewMemberFormInput,
    type NewMemberFormOutput,
    NewMemberPhoneNumber,
    phoneNumberInUseMessage,
} from "./newMember.util";

/**
 * Checks after typing stops whether an existing member already has the email address
 * or phone number, which would otherwise only fail on submit. The form's schema rejects
 * the details found in use.
 */
export function useContactDetailsCheck(
    {
        control,
        getValues,
        setError,
    }: UseFormReturn<NewMemberFormInput, unknown, NewMemberFormOutput>,
    setInUse: Dispatch<SetStateAction<ContactDetailsInUse>>,
) {
    const [email, phoneNumber] = useWatch({ control, name: ["email", "phoneNumber"] });
    const debouncedEmail = useDebounce(NewMemberEmail.safeParse(email).data);
    const debouncedPhoneNumber = useDebounce(
        NewMemberPhoneNumber.safeParse(phoneNumber).data,
    );

    const { mutate: validate } = usePost("agentValidateNewMember", {
        onSuccess: (result, { json }) => {
            const emailInUse = result.emailInUse ? json.email : undefined;
            const phoneNumberInUse = result.phoneNumberInUse
                ? json.phoneNumber
                : undefined;

            setInUse((current) => ({
                emails: emailInUse ? [...current.emails, emailInUse] : current.emails,
                phoneNumbers: phoneNumberInUse
                    ? [...current.phoneNumbers, phoneNumberInUse]
                    : current.phoneNumbers,
            }));

            // The schema only sees the new details after the next render, so the errors
            // are set here for fields that still have the checked value.
            if (
                emailInUse &&
                NewMemberEmail.safeParse(getValues("email")).data === emailInUse
            ) {
                setError("email", { message: emailInUseMessage });
            }
            if (phoneNumberInUse && getValues("phoneNumber") === phoneNumberInUse) {
                setError("phoneNumber", { message: phoneNumberInUseMessage });
            }
        },
    });

    // TODO: Debounce the check itself and call it from the fields' onChange instead
    // of watching debounced values in effects, as value debouncing suits queries
    // better than mutations. TanStack DB's debounce strategies for mutations are
    // worth exploring for this.
    useEffect(() => {
        if (debouncedEmail) validate({ json: { email: debouncedEmail } });
    }, [debouncedEmail, validate]);

    useEffect(() => {
        if (debouncedPhoneNumber) {
            validate({ json: { phoneNumber: debouncedPhoneNumber } });
        }
    }, [debouncedPhoneNumber, validate]);
}
